import axiosInstance from '../api/axiosInstance';
import { API_ENDPOINTS } from '../api/config';

// ============================================================
// INTERFACES
// ============================================================

export interface MoodCheckin {
  id: number;
  date: string;
  mood: number; // 1-5
  mood_display?: string;
  energy_level?: number;
  energy_level_display?: string;
  sleep_quality?: number;
  sleep_quality_display?: string;
  stress_level?: number;
  stress_level_display?: string;
  gratitude_note?: string;
  notes?: string;
  created_at: string;
}

export interface BreathingSession {
  id: number;
  technique: string;
  technique_display?: string;
  date: string;
  duration_seconds: number;
  cycles_completed: number;
  mood_before?: number;
  mood_after?: number;
  created_at: string;
}

export interface ScreeningAssessment {
  id: number;
  screening_type: string;
  screening_type_display?: string;
  date: string;
  answers: Record<string, number>;
  total_score?: number;
  severity?: string;
  severity_display?: string;
  flagged: boolean;
  created_at: string;
}

export interface SelfCareRecommendation {
  id: number;
  category: string;
  category_display?: string;
  title: string;
  description: string;
  action_steps: string[];
  priority: string;
  is_completed: boolean;
  completed_at?: string;
  triggered_by?: string;
  created_at: string;
}

export interface GenerateRecommendationsResponse {
  message: string;
  new_count: number;
  new_recommendations: SelfCareRecommendation[];
  recommendations: SelfCareRecommendation[];
}

export interface MentalHealthScore {
  score: number;
  category: string; // 'green' | 'amber' | 'red'
  breakdown: {
    mood: number;
    screening: number;
    hra: number;
    stress: number;
  };
  nudges: string[];
}

export interface MoodStats {
  avg_mood: number;
  avg_energy: number;
  avg_sleep: number;
  avg_stress: number;
  total_entries: number;
}

export interface ScreeningQuestionsGroup {
  title: string;
  description: string;
  questions: { number: number; text: string }[];
  answer_options: { value: number; label: string }[];
}

export interface ScreeningQuestions {
  phq2: ScreeningQuestionsGroup;
  phq9: ScreeningQuestionsGroup;
  gad7: ScreeningQuestionsGroup;
}

export interface TeamComparison {
  insufficient_data: boolean;
  min_participants?: number;
  current_participants?: number;
  participants?: number;
  team_averages?: { mood: number | null; energy: number | null; sleep: number | null; stress: number | null };
  your_averages?: { mood: number | null; energy: number | null; sleep: number | null; stress: number | null };
}

export interface TeamAggregateData {
  period: string;
  total_employees: number;
  mood_checkin: {
    participants: number;
    participation_rate: number;
    averages: { mood: number | null; energy: number | null; sleep: number | null; stress: number | null };
    total_entries: number;
    mood_distribution: Record<number, number>;
  };
  weekly_trends: { week_start: string; week_end: string; avg_mood: number | null; entries: number }[];
  screening_summary: Record<string, {
    total_taken: number;
    participants: number;
    avg_score: number;
    flagged_count: number;
    severity_distribution: Record<string, number>;
  }>;
  breathing: { participants: number; total_minutes: number };
}

export interface DashboardData {
  score_card?: MentalHealthScore;
  mood_stats?: MoodStats;
  streak?: number;
  latest_screenings?: Record<string, ScreeningAssessment>;
  breathing_stats?: { total_sessions: number; total_minutes: number };
  recommendations_count?: number;
  mood_trends?: { date: string; mood: number }[];
  team_comparison?: TeamComparison;
}

// ============================================================
// HELPER
// ============================================================

const extractData = (res: any) => res.data?.results ?? res.data;
const extractSingle = (res: any) => res.data;

// ============================================================
// SERVICE
// ============================================================

const mentalWellnessService = {
  // ------ Mood Check-ins ------
  getMoodCheckins: async (): Promise<MoodCheckin[]> =>
    extractData(await axiosInstance.get(API_ENDPOINTS.mentalWellness.moodCheckins)),

  createMoodCheckin: async (data: Partial<MoodCheckin>): Promise<MoodCheckin> =>
    extractSingle(await axiosInstance.post(API_ENDPOINTS.mentalWellness.moodCheckins, data)),

  updateMoodCheckin: async (id: number, data: Partial<MoodCheckin>): Promise<MoodCheckin> =>
    extractSingle(await axiosInstance.patch(API_ENDPOINTS.mentalWellness.moodCheckinDetail(id), data)),

  deleteMoodCheckin: async (id: number): Promise<void> =>
    axiosInstance.delete(API_ENDPOINTS.mentalWellness.moodCheckinDetail(id)),

  getCalendarHeatmap: async (startDate: string, endDate: string): Promise<any> =>
    extractSingle(await axiosInstance.get(API_ENDPOINTS.mentalWellness.moodCalendar, { params: { start_date: startDate, end_date: endDate } })),

  getMoodStreak: async (): Promise<{ current_streak: number; longest_streak: number }> =>
    extractSingle(await axiosInstance.get(API_ENDPOINTS.mentalWellness.moodStreak)),

  getMoodStats: async (period?: string): Promise<MoodStats> =>
    extractSingle(await axiosInstance.get(API_ENDPOINTS.mentalWellness.moodStats, { params: period ? { period } : {} })),

  getMoodTrends: async (period?: string): Promise<{ date: string; mood: number }[]> =>
    extractData(await axiosInstance.get(API_ENDPOINTS.mentalWellness.moodTrends, { params: period ? { period } : {} })),

  getMoodChoices: async (): Promise<any> =>
    extractSingle(await axiosInstance.get(API_ENDPOINTS.mentalWellness.moodChoices)),

  // ------ Breathing Sessions ------
  getBreathingSessions: async (): Promise<BreathingSession[]> =>
    extractData(await axiosInstance.get(API_ENDPOINTS.mentalWellness.breathingSessions)),

  createBreathingSession: async (data: Partial<BreathingSession>): Promise<BreathingSession> =>
    extractSingle(await axiosInstance.post(API_ENDPOINTS.mentalWellness.breathingSessions, data)),

  getBreathingStats: async (): Promise<{ total_sessions: number; total_minutes: number }> =>
    extractSingle(await axiosInstance.get(API_ENDPOINTS.mentalWellness.breathingStats)),

  // ------ Screening Assessments ------
  getScreenings: async (): Promise<ScreeningAssessment[]> =>
    extractData(await axiosInstance.get(API_ENDPOINTS.mentalWellness.screenings)),

  createScreening: async (data: Partial<ScreeningAssessment>): Promise<ScreeningAssessment> =>
    extractSingle(await axiosInstance.post(API_ENDPOINTS.mentalWellness.screenings, data)),

  getScreeningQuestions: async (): Promise<ScreeningQuestions> =>
    extractSingle(await axiosInstance.get(API_ENDPOINTS.mentalWellness.screeningQuestions)),

  getLatestScreenings: async (): Promise<Record<string, ScreeningAssessment>> =>
    extractSingle(await axiosInstance.get(API_ENDPOINTS.mentalWellness.screeningLatest)),

  getScreeningTrends: async (type: string): Promise<any> =>
    extractData(await axiosInstance.get(API_ENDPOINTS.mentalWellness.screeningTrends, { params: { type } })),

  // ------ Recommendations ------
  getRecommendations: async (): Promise<SelfCareRecommendation[]> =>
    extractData(await axiosInstance.get(API_ENDPOINTS.mentalWellness.recommendations)),

  completeRecommendation: async (id: number): Promise<SelfCareRecommendation> =>
    extractSingle(await axiosInstance.post(API_ENDPOINTS.mentalWellness.recommendationComplete(id))),

  generateRecommendations: async (): Promise<GenerateRecommendationsResponse> => {
    const res = await axiosInstance.post(API_ENDPOINTS.mentalWellness.recommendationGenerate);
    const data = res.data;
    // Backend (new shape) returns an object; older deployments may still return an array.
    if (Array.isArray(data)) {
      return {
        message: data.length > 0 ? `Generated ${data.length} new recommendation${data.length !== 1 ? 's' : ''}.` : 'No new recommendations to generate.',
        new_count: data.length,
        new_recommendations: data,
        recommendations: data,
      };
    }
    return {
      message: data.message || 'Recommendations updated.',
      new_count: data.new_count ?? (data.new_recommendations?.length ?? 0),
      new_recommendations: data.new_recommendations ?? [],
      recommendations: data.recommendations ?? data.results ?? [],
    };
  },

  // ------ Score & Dashboard ------
  getMentalHealthScore: async (): Promise<MentalHealthScore> =>
    extractSingle(await axiosInstance.get(API_ENDPOINTS.mentalWellness.score)),

  getDashboard: async (): Promise<DashboardData> =>
    extractSingle(await axiosInstance.get(API_ENDPOINTS.mentalWellness.dashboard)),

  // ------ Team Aggregate (HR only) ------
  getTeamAggregate: async (period: string = '30d'): Promise<TeamAggregateData> =>
    extractSingle(await axiosInstance.get(API_ENDPOINTS.mentalWellness.teamAggregate, { params: { period } })),
};

export default mentalWellnessService;
